import Fuse from "fuse.js";
import { state } from "./state";
import { latestVersionByPrefab } from "./model";
import type { Asset, Prefab } from "./model";

/**
 * Assets belonging only to each prefab's latest revision. Browsing and
 * searching the asset view should never surface the same asset once per
 * revision, so the grid and the Fuse index both draw from this deduped set.
 * (The prefab detail panel lists a chosen revision's own assets separately.)
 */
let assetsLatest: Asset[] = [];

export function initFuse(): void {
  const index = state.index!;
  const latest = latestVersionByPrefab(index.prefabs);
  assetsLatest = index.assets.filter((a) => a.version === latest.get(a.prefab));
  state.fuseAssets = new Fuse(assetsLatest, {
    keys: [
      { name: "id", weight: 2 },
      { name: "tags", weight: 3 },
      { name: "description", weight: 1 },
      { name: "category", weight: 1 },
    ],
    threshold: 0.4,
    ignoreLocation: true,
  });
  state.fusePrefabs = new Fuse(index.prefabs, {
    keys: [
      { name: "id", weight: 2 },
      { name: "tags", weight: 2 },
      { name: "description", weight: 1 },
      { name: "category", weight: 1 },
    ],
    threshold: 0.4,
    ignoreLocation: true,
  });
}

/** Apply search (Fuse) then filters (AND across groups, OR within). */
export function filteredItems(): (Asset | Prefab)[] {
  const index = state.index!;
  let items: (Asset | Prefab)[];
  if (state.view === "assets") {
    items = state.search.trim()
      ? state.fuseAssets!.search(state.search).map((r) => r.item)
      : assetsLatest;
  } else {
    items = state.search.trim()
      ? state.fusePrefabs!.search(state.search).map((r) => r.item)
      : index.prefabs;
  }

  const { kinds, categories, tags } = state.filters;
  return items.filter((item) => {
    if (state.view === "assets" && kinds.size > 0) {
      if (!kinds.has((item as Asset).kind)) return false;
    }
    if (categories.size > 0) {
      if (!item.category || !categories.has(item.category)) return false;
    }
    if (tags.size > 0) {
      const itemTags = new Set(item.tags ?? []);
      let hit = false;
      for (const t of tags) {
        if (itemTags.has(t)) {
          hit = true;
          break;
        }
      }
      if (!hit) return false;
    }
    return true;
  });
}
